import { ArchiveRestore } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { hasPermission, useClaims } from "@/hooks/use-claims";
import { useTranslation } from "@/i18n/use-translation";
import { useAdminOrgStore } from "@/stores/admin-org";

import { BackLink, LoadError, PickASite, RecipeCount, RowsSkeleton, useOpen } from "./menu-parts";
import {
  CADENCE_LABELS,
  datesLabel,
  daysBetween,
  menuLabel,
  shortDate,
  type Menu,
} from "./menu-model";
import { SetUpDialog, needsSetUp } from "./set-up-dialog";
import { useMenuActions, useSiteMenus, useSiteToday } from "./use-menus";

/** Archived menus at one site, each opening its recipes and with Restore (POS2-1456). Replaces
 * the "Archived" filter. */

const AUTO_ARCHIVE_DAYS = 90;

export default function MenusArchivePage() {
  const { t } = useTranslation();
  const siteId = useAdminOrgStore((s) => s.siteId);
  const siteName = useAdminOrgStore((s) => s.siteName);
  return (
    <div className="mx-auto max-w-5xl">
      <BackLink label={t("menus.title")} to="/menus" />
      <h1 className="text-lg font-medium text-gray-900">{t("menus.archived.title")}</h1>
      {siteId ? (
        <>
          <p className="mb-6 text-sm text-gray-500">
            {t("menus.archived.description", { site: siteName })}
          </p>
          <ArchivedMenus key={siteId} siteId={siteId} />
        </>
      ) : (
        <div className="mt-6">
          <PickASite />
        </div>
      )}
    </div>
  );
}

function ArchivedMenus({ siteId }: { siteId: string }) {
  const { t, locale } = useTranslation();
  const { isPlatformAdmin, permissions } = useClaims();
  const canRestore = isPlatformAdmin || hasPermission(permissions, "menu:create");
  const archived = useSiteMenus(siteId, true);
  // The set-up form's "Comes after" offers the site's current menus.
  const current = useSiteMenus(siteId, false);
  const today = useSiteToday(siteId);
  const { restore } = useMenuActions();
  const navigate = useNavigate();
  const openPage = useOpen();
  const [settingUp, setSettingUp] = useState<Menu | null>(null);

  if (archived.isPending || current.isPending || today === null) return <RowsSkeleton />;
  if (archived.isError) return <LoadError onRetry={() => archived.refetch()} />;
  if (current.isError) return <LoadError onRetry={() => current.refetch()} />;
  const rows = [...archived.data].sort((a, b) => b.archived_at!.localeCompare(a.archived_at!));
  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-black/[0.06] bg-white p-10 text-center text-sm text-gray-500">
        {t("menus.archived.empty")}
      </p>
    );
  }

  // A menu opens read-only, so its recipes can be seen before it is restored.
  const open = (menu: Menu) =>
    openPage(`/menus/${menu.id}?from=archive`, t("menus.archived.title"));

  function archivedLine(menu: Menu): string {
    const day = menu.archived_at!.slice(0, 10);
    const date = shortDate(locale, day);
    const automatic = menu.ends_on !== null && daysBetween(menu.ends_on, day) >= AUTO_ARCHIVE_DAYS;
    return automatic
      ? t("menus.archived.automatically", { date })
      : t("menus.archived.byHand", { date });
  }

  return (
    <>
      <div className="divide-y divide-black/[0.06] overflow-hidden rounded-lg border border-black/[0.06] bg-white">
        {rows.map((menu) => {
          const label = menuLabel(t, locale, menu);
          const dates = datesLabel(locale, menu);
          const cadence = t(CADENCE_LABELS[menu.cadence]);
          return (
            <div
              key={menu.id}
              role="button"
              tabIndex={0}
              onClick={() => open(menu)}
              onKeyDown={(e) => e.key === "Enter" && open(menu)}
              className="flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors duration-[120ms] hover:bg-black/[0.02]"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{label}</p>
                <p className="truncate text-xs text-gray-400">
                  {dates ? `${cadence}. ${dates}` : cadence}
                </p>
                <p className="text-xs text-gray-500 sm:hidden">{archivedLine(menu)}</p>
              </div>
              <div className="hidden w-72 shrink-0 text-sm text-gray-500 sm:block">
                {archivedLine(menu)}
              </div>
              <div className="hidden w-12 shrink-0 justify-end sm:flex">
                <RecipeCount count={menu.recipe_count} />
              </div>
              {canRestore && (
                // Wide enough for the longest label (German), so every row's dates and counts line up.
                <div className="flex shrink-0 justify-end sm:w-64">
                  {needsSetUp(menu) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-600"
                      aria-label={t("menus.archived.setUpMenu", { name: label })}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSettingUp(menu);
                      }}
                    >
                      <ArchiveRestore />
                      <span className="hidden sm:inline">{t("menus.archived.setUp")}</span>
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-600"
                      disabled={restore.isPending}
                      aria-label={t("menus.archived.restoreMenu", { name: label })}
                      onClick={(e) => {
                        e.stopPropagation();
                        restore.mutate(menu.id, {
                          onSuccess: () =>
                            toast.success(t("menus.archived.restored", { name: label })),
                        });
                      }}
                    >
                      <ArchiveRestore />
                      <span className="hidden sm:inline">{t("menus.restore")}</span>
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {settingUp && (
        <SetUpDialog
          menu={settingUp}
          hasRecipes={settingUp.recipe_count > 0}
          menus={current.data}
          siteId={siteId}
          today={today}
          onClose={() => setSettingUp(null)}
          onRestored={(reading) => navigate(`/menus/${settingUp.id}`, { state: { reading } })}
        />
      )}
    </>
  );
}
